import { useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Alert,
  useTheme,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import ml from "../images/MercadoLibre.svg";
import ConnectionsList from "./connectionsList";

const ML_REDIRECT_URI =
  process.env.REACT_APP_ML_REDIRECT_URL ||
  `${(process.env.REACT_APP_API_URL || "").replace(/\/$/, "")}/integrations-api/mercadolibre/callback`;

const MercadoLibreModal = ({ open, onClose, onSave, connections = [], onDelete }) => {
  const theme = useTheme();
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleCopy = () => {
    navigator.clipboard.writeText(ML_REDIRECT_URI);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSave = async () => {
    if (!clientId || !clientSecret) return;
    setLoading(true);
    setErrorMessage("");
    try {
      await onSave?.({ clientId, clientSecret });
      handleClose();
    } catch (error) {
      const apiMessage = error?.response?.data?.detail || error?.response?.data?.message;
      setErrorMessage(apiMessage || "No se pudo iniciar la conexión con MercadoLibre.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setClientId("");
    setClientSecret("");
    setShowSecret(false);
    setErrorMessage("");
    onClose();
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: theme.palette.background.paper,
      "& fieldset": { borderColor: theme.palette.divider },
      "&:hover fieldset": { borderColor: theme.palette.text.secondary },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: "1.5px",
      },
      "& input": { color: theme.palette.text.primary, fontSize: "0.88rem" },
      "& input::placeholder": { color: theme.palette.text.disabled },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary,
      fontSize: "0.72rem",
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      "&.Mui-focused": { color: theme.palette.primary.main },
    },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          backgroundColor: theme.palette.background.default,
          boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
          overflow: "hidden",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Header */}
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            borderBottom: `1px solid ${theme.palette.divider}`,
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LinkIcon sx={{ color: theme.palette.text.secondary, fontSize: 15 }} />
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.68rem",
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
              }}
            >
              MERCADOLIBRE
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              "&:hover": { color: theme.palette.text.primary },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, py: 3 }}>
          {/* Integration card */}
          <Box
            sx={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "14px",
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "10px",
                  backgroundColor: "#FFE600",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                <Box
                  component="img"
                  src={ml}
                  alt="MercadoLibre"
                  sx={{ width: 32, height: 32, objectFit: "contain" }}
                />
              </Box>
              <Box>
                <Typography sx={{ color: theme.palette.text.primary, fontWeight: 600, fontSize: "0.95rem" }}>
                  MercadoLibre
                </Typography>
                <Typography sx={{ color: theme.palette.text.secondary, fontSize: "0.78rem" }}>
                  Falta configurar credenciales de tu app MeLi
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Existing connections */}
          <ConnectionsList connections={connections} onDelete={onDelete} />

          {/* Credentials section */}
          <Box
            sx={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "14px",
              p: 2.5,
            }}
          >
            <Typography sx={{ color: theme.palette.text.primary, fontWeight: 600, fontSize: "0.9rem", mb: 2 }}>
              {connections.length > 0 ? "Agregar otra cuenta de MeLi" : "Credenciales de tu app MeLi"}
            </Typography>

            {/* Steps */}
            <Typography sx={{ color: theme.palette.text.secondary, fontSize: "0.82rem", mb: 0.5 }}>
              1. Andá a{" "}
              <Box
                component="a"
                href="https://developers.mercadolibre.com.ar/devcenter"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: theme.palette.primary.main,
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                developers.mercadolibre.com.ar/devcenter
              </Box>{" "}
              y creá una aplicación nueva.
            </Typography>
            <Typography sx={{ color: theme.palette.text.secondary, fontSize: "0.82rem", mb: 1.5 }}>
              2. En el setup de la app, poné esta{" "}
              <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 700 }}>
                Redirect URI
              </Box>
              :
            </Typography>

            {/* Redirect URI */}
            <Box
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                border: `1px dashed ${theme.palette.divider}`,
                borderRadius: "8px",
                px: 2,
                py: 1.2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
                cursor: "pointer",
              }}
              onClick={handleCopy}
            >
              <Typography
                sx={{
                  fontFamily: "'Courier New', monospace",
                  fontSize: "0.78rem",
                  color: theme.palette.primary.main,
                  userSelect: "all",
                }}
              >
                {ML_REDIRECT_URI}
              </Typography>
              <IconButton
                size="small"
                sx={{
                  color: copied ? theme.palette.success.main : theme.palette.text.secondary,
                  ml: 1,
                }}
              >
                {copied ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Box>

            <Typography sx={{ color: theme.palette.text.secondary, fontSize: "0.82rem", mb: 2 }}>
              3. Copiá el{" "}
              <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 700 }}>Client ID
              </Box>{" "}
              y el{" "}
              <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 700 }}>Client Secret
              </Box>{" "}
              que te da MeLi y pegalos abajo.
            </Typography>

            {errorMessage ? (
              <Alert
                severity="error"
                sx={{
                  mb: 2.5,
                  borderRadius: "10px",
                }}
              >
                {errorMessage}
              </Alert>
            ) : null}

            {/* Security note */}
            <Alert
              icon={false}
              sx={{
                backgroundColor: alpha(theme.palette.success.main, 0.08),
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                borderRadius: "10px",
                mb: 2.5,
                py: 1,
                "& .MuiAlert-message": {
                  color: theme.palette.text.secondary,
                  fontSize: "0.75rem",
                  lineHeight: 1.5,
                },
              }}
            >
              🔒{" "}
              <Box component="span" sx={{ color: theme.palette.success.main, fontWeight: 700 }}>
                Seguridad:
              </Box>{" "}
              tus credenciales se cifran con AES-256 antes de guardarse en la base. Nunca las devolvemos en texto plano ni las loggeamos. Solo el proceso server-side las descifra para autenticar con MeLi en tu nombre.
            </Alert>

            {/* Inputs */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="CLIENT ID (APP ID)"
                placeholder="1234567890123456"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                fullWidth
                size="small"
                sx={inputSx}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="CLIENT SECRET"
                placeholder="••••••••••••••••••••"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                type={showSecret ? "text" : "password"}
                fullWidth
                size="small"
                sx={inputSx}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowSecret(!showSecret)}
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {showSecret ? (
                          <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} />
                        ) : (
                          <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Actions */}
            <Box sx={{ display: "flex", gap: 1.5, mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={loading || !clientId || !clientSecret}
                disableElevation
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  px: 2.5,
                  "&:hover": { backgroundColor: theme.palette.primary.dark },
                  "&.Mui-disabled": {
                    backgroundColor: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                  },
                }}
              >
                Guardar credenciales
              </Button>
              <Button
                variant="outlined"
                onClick={handleClose}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.secondary,
                  px: 2.5,
                  "&:hover": {
                    borderColor: theme.palette.text.secondary,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  },
                }}
              >
                Cancelar
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default MercadoLibreModal;